import { useEffect, useState, type FormEvent } from 'react';
import {
  ArrowLeft, ShieldCheck, AlertCircle, ShieldAlert, Lock, LifeBuoy, Info, Loader2,
  Handshake, Phone, MessageCircle, KeyRound, AlertTriangle, Copy, Printer, Download, Check,
  PartyPopper, ArrowRight, LogIn,
} from 'lucide-react';
import { api, ApiError, setToken } from '../api/client';
import { Button, Field, Input } from '../components/ui';
import { PasswordInput, PasswordStrength, PasswordTip, scorePassword } from '../components/PasswordInput';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../api/queries';
import { DatenschutzModal, ImpressumModal } from '../components/LegalModals';
import type { CurrentUser } from '../api/types';

type Mode = 'login' | 'forgot' | 'recovery' | 'newpw' | 'reveal' | 'setup';

const CONTACTS = {
  obmann: { name: 'Maximilian Huber', telefon: '+43 664 1234567', whatsapp: true },
  kassier: { name: 'Bernhard Lindner', telefon: '+43 664 5559021', whatsapp: false },
};

export function AuthFlow({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [mode, setMode] = useState<Mode>('login');
  const [ctx, setCtx] = useState<{ user?: CurrentUser; codes?: string[]; pwMode?: PwMode }>({});

  if (mode === 'login') {
    return (
      <LoginScreen
        onForgot={() => setMode('forgot')}
        onRecovery={() => setMode('recovery')}
        onSetup={() => setMode('setup')}
        onSuccess={(u) => {
          if (u.isTemp) {
            setCtx({ user: u, pwMode: 'firstlogin' });
            setMode('newpw');
          } else onAuthenticated();
        }}
      />
    );
  }
  if (mode === 'forgot') return <ForgotScreen onBack={() => setMode('login')} onRecovery={() => setMode('recovery')} />;
  if (mode === 'recovery') {
    return (
      <RecoveryScreen
        onBack={() => setMode('login')}
        onSuccess={(u) => { setCtx({ user: u, pwMode: 'recovery' }); setMode('newpw'); }}
      />
    );
  }
  if (mode === 'newpw') {
    return (
      <NewPasswordScreen
        ctxMode={ctx.pwMode ?? 'firstlogin'}
        user={ctx.user!}
        onBack={() => setMode('login')}
        onDone={(u) => {
          if (ctx.pwMode === 'firstlogin' && u.rolle === 'admin') {
            // generate recovery codes once
            api.post<{ codes: string[] }>('/auth/recovery-codes/generate').then((r) => {
              setCtx((c) => ({ ...c, codes: r.codes }));
              setMode('reveal');
            }).catch(() => onAuthenticated());
          } else {
            onAuthenticated();
          }
        }}
      />
    );
  }
  if (mode === 'reveal') {
    return <RecoveryCodesReveal codes={ctx.codes ?? []} onDone={onAuthenticated} />;
  }
  if (mode === 'setup') return <SetupWizard onBack={() => setMode('login')} onComplete={onAuthenticated} />;
  return null;
}

// ----------------------------------------------------------------
// 1. Login
// ----------------------------------------------------------------
function LoginScreen({
  onForgot, onRecovery, onSetup, onSuccess,
}: {
  onForgot: () => void; onRecovery: () => void; onSetup: () => void;
  onSuccess: (user: CurrentUser) => void;
}) {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [remember, setRemember] = useState(true);
  const [showDemo, setShowDemo] = useState(false);   // TESTPHASE — s. unten
  const [error, setError] = useState<null | 'wrong' | 'locked' | 'deactivated'>(null);
  const [attempts, setAttempts] = useState(0);
  const [lockSec, setLockSec] = useState(0);
  const [loading, setLoading] = useState(false);
  const qc = useQueryClient();

  useEffect(() => {
    if (lockSec <= 0) return;
    const t = setInterval(() => setLockSec((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [lockSec]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (lockSec > 0) return;
    setLoading(true); setError(null);
    try {
      const r = await api.post<{ user: CurrentUser; token?: string }>('/auth/login', { email, passwort: pw, remember });
      if (r.token) setToken(r.token);
      qc.invalidateQueries({ queryKey: queryKeys.me });
      onSuccess(r.user);
    } catch (err) {
      if (err instanceof ApiError) {
        const code = err.payload?.error;
        if (code === 'deactivated') setError('deactivated');
        else if (code === 'locked') { setError('locked'); setLockSec(err.payload?.lockSec ?? 300); }
        else {
          setError('wrong');
          if (err.payload?.attempts) setAttempts(err.payload.attempts);
          else setAttempts((a) => a + 1);
        }
      } else setError('wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-shell">
      <form className="auth-card" onSubmit={submit}>
        <div className="auth-logo"><img src="/logo.png" alt="" /></div>
        <div style={{ textAlign: 'center' }}>
          <h1 className="t-title-1" style={{ margin: 0 }}>Union DSG Gaspoltshofen</h1>
          <div className="text-tertiary" style={{ marginTop: 6, fontSize: 15 }}>Mitgliederverwaltung</div>
          <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-tertiary)' }}>
            <ShieldCheck size={13} /> Sichere Anmeldung · Daten in der EU
          </div>
        </div>

        {error === 'deactivated' && (
          <div className="banner danger" style={{ margin: 0 }}>
            <Lock size={18} />
            <div className="banner-text">Dein Account ist aktuell deaktiviert. Bitte sprich mit dem Obmann.</div>
          </div>
        )}
        {error === 'locked' && (
          <div className="banner danger" style={{ margin: 0 }}>
            <ShieldAlert size={18} />
            <div className="banner-text">
              Zu viele Versuche – bitte warte {Math.floor(lockSec / 60)}:{String(lockSec % 60).padStart(2, '0')} und versuche es nochmal.{' '}
              <button type="button" onClick={onForgot} style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>
                Stattdessen über den Obmann zurücksetzen
              </button>
            </div>
          </div>
        )}
        {error === 'wrong' && (
          <div className="banner warn" style={{ margin: 0 }}>
            <AlertCircle size={18} />
            <div className="banner-text">
              E-Mail oder Passwort falsch.
              {attempts >= 3 && ` ${5 - attempts} weitere${5 - attempts === 1 ? 'r' : ''} Versuch${5 - attempts === 1 ? '' : 'e'} bis zur Sperrung.`}
            </div>
          </div>
        )}

        <Field label="E-Mail">
          <Input type="email" size="lg" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
        </Field>
        <Field label="Passwort">
          <PasswordInput value={pw} onChange={setPw} placeholder="Dein Passwort" />
        </Field>

        <div className="row" style={{ justifyContent: 'space-between' }}>
          <label className="row" style={{ gap: 8, cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 13.5 }}>
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--accent)' }} />
            Eingeloggt bleiben
          </label>
          <button type="button" onClick={onForgot} style={{ color: 'var(--accent)', fontSize: 13.5, fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer' }}>
            Passwort vergessen?
          </button>
        </div>

        <Button variant="primary" size="lg" type="submit" disabled={loading || lockSec > 0}>
          {loading && <Loader2 size={17} className="spin" />}
          {lockSec > 0
            ? `Gesperrt (${Math.floor(lockSec / 60)}:${String(lockSec % 60).padStart(2, '0')})`
            : 'Anmelden'}
        </Button>

        <button type="button" onClick={onRecovery} style={{ fontSize: 13, color: 'var(--text-tertiary)', display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer' }}>
          <LifeBuoy size={13} /> Recovery-Code verwenden
        </button>

        {/* TESTPHASE — vor dem Echtbetrieb ENTFERNEN.
            Diese Zugangsdaten sind für jeden sichtbar, der die Seite aufruft. */}
        <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
          <button
            type="button"
            onClick={() => setShowDemo((v) => !v)}
            style={{ fontSize: 12, color: 'var(--text-quaternary)', display: 'flex', alignItems: 'center', gap: 5, margin: '0 auto', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <Info size={12} /> Testzugang {showDemo ? 'ausblenden' : 'anzeigen'}
          </button>
          {showDemo && (
            <div className="banner warn" style={{ marginTop: 10, fontSize: 12.5 }}>
              <AlertTriangle size={16} />
              <div className="banner-text">
                <strong>Nur für die Testphase.</strong><br />
                m.huber@union-gaspoltshofen.at · Passwort: Gaspolts2026!
                <div style={{ marginTop: 6, opacity: 0.85 }}>
                  Sobald echte Mitgliederdaten erfasst werden, muss dieser Hinweis weg und
                  das Passwort geändert werden — jeder Besucher der Seite sieht ihn.
                </div>
              </div>
            </div>
          )}
        </div>
      </form>

      <LoginFooter onSetup={onSetup} />
    </div>
  );
}

function LoginFooter({ onSetup }: { onSetup: () => void }) {
  const [datenschutzOpen, setDatenschutzOpen] = useState(false);
  const [impressumOpen, setImpressumOpen] = useState(false);
  return (
    <>
      <div style={{ position: 'absolute', bottom: 22, left: 0, right: 0, textAlign: 'center', fontSize: 12.5, color: 'var(--text-quaternary)', display: 'flex', gap: 14, justifyContent: 'center' }}>
        <button onClick={() => setImpressumOpen(true)} style={{ color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer' }}>Impressum</button>
        <button onClick={() => setDatenschutzOpen(true)} style={{ color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer' }}>Datenschutz</button>
        <button onClick={onSetup} style={{ color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer' }}>Ersteinrichtung</button>
      </div>
      <DatenschutzModal open={datenschutzOpen} onClose={() => setDatenschutzOpen(false)} />
      <ImpressumModal open={impressumOpen} onClose={() => setImpressumOpen(false)} />
    </>
  );
}

// ----------------------------------------------------------------
// 2. Forgot
// ----------------------------------------------------------------
function ForgotScreen({ onBack, onRecovery }: { onBack: () => void; onRecovery: () => void }) {
  return (
    <div className="login-shell">
      <div className="auth-card">
        <button className="auth-back" onClick={onBack} type="button" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
          <ArrowLeft size={15} /> Zurück zum Login
        </button>
        <div style={{ width: 52, height: 52, borderRadius: 14, background: 'var(--accent-soft)', color: 'var(--accent)', display: 'grid', placeItems: 'center', margin: '0 auto' }}>
          <Handshake size={26} />
        </div>
        <div style={{ textAlign: 'center' }}>
          <h1 className="t-title-2" style={{ margin: 0 }}>Passwort-Reset über deinen Obmann</h1>
          <p className="text-secondary" style={{ marginTop: 8, fontSize: 14, lineHeight: 1.5 }}>
            Wir senden keine automatischen Reset-Mails. Stattdessen kannst du:
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="muted-card" style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <Phone size={16} className="text-accent" style={{ flexShrink: 0, marginTop: 1 }} />
            <span style={{ fontSize: 13.5 }}>
              <strong style={{ color: 'var(--text-primary)' }}>Den Obmann oder Kassier kontaktieren</strong> – sie können dir ein neues Passwort generieren.
            </span>
          </div>
          <div className="muted-card" style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <LifeBuoy size={16} className="text-accent" style={{ flexShrink: 0, marginTop: 1 }} />
            <span style={{ fontSize: 13.5 }}>
              <strong style={{ color: 'var(--text-primary)' }}>Einen Recovery-Code verwenden</strong>, falls du welche bei der Anmeldung erhalten hast.
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(['obmann', 'kassier'] as const).map((k) => {
            const c = CONTACTS[k];
            return (
              <div className="contact-row" key={k}>
                <div className="ava">{c.name.split(' ').map((x) => x[0]).join('')}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 500 }}>{k === 'obmann' ? 'Obmann' : 'Kassier'}: {c.name}</div>
                  <div className="text-tertiary t-mono" style={{ fontSize: 12.5 }}>{c.telefon}</div>
                </div>
                <a href={`tel:${c.telefon.replace(/\s/g, '')}`} className="btn btn-secondary btn-sm"><Phone size={14} /></a>
                {c.whatsapp && <a href="#" className="btn btn-secondary btn-sm"><MessageCircle size={14} /></a>}
              </div>
            );
          })}
        </div>

        <Button variant="primary" icon={<LifeBuoy size={16} />} onClick={onRecovery}>Recovery-Code verwenden</Button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
// 3. Recovery code
// ----------------------------------------------------------------
function RecoveryScreen({ onBack, onSuccess }: { onBack: () => void; onSuccess: (u: CurrentUser) => void }) {
  const [email, setEmail] = useState('');
  const [parts, setParts] = useState(['', '', '']);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const refs = [
    { current: null as HTMLInputElement | null },
    { current: null as HTMLInputElement | null },
    { current: null as HTMLInputElement | null },
  ];

  const setPart = (i: number, v: string) => {
    const clean = v.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 4);
    const next = [...parts] as [string, string, string];
    next[i] = clean;
    setParts(next);
    setError(false);
    if (clean.length === 4 && i < 2) refs[i + 1]!.current?.focus();
  };

  const onPaste = (e: React.ClipboardEvent) => {
    const txt = (e.clipboardData.getData('text') || '').toUpperCase().replace(/[^0-9A-Z]/g, '');
    if (txt.length >= 8) {
      e.preventDefault();
      setParts([txt.slice(0, 4), txt.slice(4, 8), txt.slice(8, 12)]);
      refs[2]!.current?.focus();
    }
  };

  const onKey = (i: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !parts[i] && i > 0) refs[i - 1]!.current?.focus();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true); setError(false);
    try {
      const r = await api.post<{ user: CurrentUser; codesLeft: number; token?: string }>('/auth/recovery', {
        email, code: parts.join(''),
      });
      if (r.token) setToken(r.token);
      onSuccess(r.user);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-shell">
      <form className="auth-card" onSubmit={submit} onPaste={onPaste}>
        <button className="auth-back" onClick={onBack} type="button" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
          <ArrowLeft size={15} /> Zurück zum Login
        </button>
        <div style={{ width: 52, height: 52, borderRadius: 14, background: 'var(--accent-soft)', color: 'var(--accent)', display: 'grid', placeItems: 'center', margin: '0 auto' }}>
          <KeyRound size={26} />
        </div>
        <div style={{ textAlign: 'center' }}>
          <h1 className="t-title-2" style={{ margin: 0 }}>Mit Recovery-Code einloggen</h1>
          <p className="text-secondary" style={{ marginTop: 8, fontSize: 14, lineHeight: 1.5 }}>
            Gib einen deiner Recovery-Codes ein, die du beim ersten Login erhalten hast. Jeder Code funktioniert nur einmal.
          </p>
        </div>
        <Field label="E-Mail">
          <Input type="email" size="lg" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Recovery-Code" error={error ? 'Dieser Code ist falsch oder bereits verwendet.' : null}>
          <div className="code-inputs">
            {parts.map((p, i) => (
              <span key={i} style={{ display: 'contents' }}>
                {i > 0 && <span className="dash">–</span>}
                <input
                  ref={(el) => { refs[i]!.current = el; }}
                  value={p}
                  onChange={(e) => setPart(i, e.target.value)}
                  onKeyDown={(e) => onKey(i, e)}
                  placeholder="XXXX"
                  autoFocus={i === 0}
                  inputMode="text"
                />
              </span>
            ))}
          </div>
        </Field>
        <Button variant="primary" size="lg" type="submit" disabled={loading}>
          {loading && <Loader2 size={17} className="spin" />}
          Code verwenden
        </Button>
        <p className="text-tertiary" style={{ fontSize: 12.5, textAlign: 'center', margin: 0 }}>
          Nach erfolgreichem Login legst du gleich ein neues Passwort fest.
        </p>
      </form>
    </div>
  );
}

// ----------------------------------------------------------------
// 4. New password
// ----------------------------------------------------------------
type PwMode = 'firstlogin' | 'recovery' | 'reset' | 'selfchange';

function NewPasswordScreen({
  ctxMode, user, onBack, onDone,
}: {
  ctxMode: PwMode;
  user: CurrentUser;
  onBack: () => void;
  onDone: (u: CurrentUser) => void;
}) {
  const [cur, setCur] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const titles: Record<string, string> = {
    firstlogin: `Willkommen ${user.vorname}! Lege dein Passwort fest`,
    reset: 'Bitte setze ein neues Passwort',
    recovery: 'Lege ein neues Passwort fest',
    selfchange: 'Passwort ändern',
  };
  const strong = scorePassword(pw).score >= 2;
  const match = !!pw && pw === pw2;
  const canSave = strong && match && (ctxMode !== 'selfchange' || cur.length > 0);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    setLoading(true); setError(null);
    try {
      await api.post('/auth/password', { aktuell: cur || undefined, neu: pw });
      onDone(user);
    } catch (err) {
      if (err instanceof ApiError && err.payload?.error === 'wrong_current') setError('Das aktuelle Passwort stimmt nicht.');
      else setError('Das Passwort konnte nicht gespeichert werden.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-shell">
      <form className="auth-card" onSubmit={submit}>
        {ctxMode !== 'selfchange' && (
          <button className="auth-back" onClick={onBack} type="button" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <ArrowLeft size={15} /> Abbrechen
          </button>
        )}
        {ctxMode === 'firstlogin' && (
          <div className="banner info" style={{ margin: 0 }}>
            <Info size={18} />
            <div className="banner-text">Du verwendest noch ein temporäres Passwort. Bitte lege jetzt dein eigenes fest.</div>
          </div>
        )}
        <div style={{ textAlign: 'center' }}>
          <h1 className="t-title-2" style={{ margin: 0 }}>{titles[ctxMode]}</h1>
        </div>
        {ctxMode === 'selfchange' && (
          <Field label="Aktuelles Passwort" error={error}>
            <PasswordInput value={cur} onChange={setCur} placeholder="Dein aktuelles Passwort" autoFocus />
          </Field>
        )}
        <Field label="Neues Passwort">
          <PasswordInput value={pw} onChange={setPw} placeholder="Mindestens 8 Zeichen" autoFocus={ctxMode !== 'selfchange'} />
        </Field>
        <PasswordStrength pw={pw} />
        <PasswordTip />
        <Field label="Passwort wiederholen" error={pw2 && !match ? 'Die Passwörter stimmen nicht überein.' : null}>
          <PasswordInput value={pw2} onChange={setPw2} placeholder="Nochmal eingeben" />
        </Field>
        <Button variant="primary" size="lg" type="submit" disabled={!canSave || loading}>
          {loading && <Loader2 size={17} className="spin" />} Passwort speichern
        </Button>
      </form>
    </div>
  );
}

// ----------------------------------------------------------------
// 5. Recovery codes reveal
// ----------------------------------------------------------------
function RecoveryCodesReveal({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyAll = () => {
    navigator.clipboard?.writeText(codes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="login-shell">
      <div className="auth-card wide">
        <div className="banner warn" style={{ margin: 0 }}>
          <AlertTriangle size={18} />
          <div className="banner-text"><strong>Wichtig: Speichere diese Codes!</strong></div>
        </div>
        <p className="text-secondary" style={{ fontSize: 14, lineHeight: 1.5, margin: 0 }}>
          Diese 10 Recovery-Codes ermöglichen dir Zugang, falls du dein Passwort vergisst und kein anderer Admin verfügbar ist.{' '}
          <strong style={{ color: 'var(--text-primary)' }}>Speichere sie jetzt sicher – sie werden nur einmal angezeigt.</strong>
        </p>
        <div className="print-codes">
          <div className="codes-grid">
            {codes.map((c, i) => (
              <div className="code-cell" key={i}><span className="idx">{i + 1}</span>{c}</div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="secondary" icon={copied ? <Check size={16} /> : <Copy size={16} />} onClick={copyAll}>
            {copied ? 'Kopiert' : 'Alle kopieren'}
          </Button>
          <Button variant="secondary" icon={<Printer size={16} />} onClick={() => window.print()}>Drucken</Button>
          <Button variant="secondary" icon={<Download size={16} />} onClick={() => window.print()}>Als PDF</Button>
        </div>
        <label className="row" style={{ gap: 10, cursor: 'pointer', padding: '10px 12px', background: 'var(--bg-muted)', borderRadius: 10 }}>
          <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} style={{ width: 18, height: 18, accentColor: 'var(--accent)' }} />
          <span style={{ fontSize: 14, fontWeight: 500 }}>Ich habe die Codes sicher gespeichert</span>
        </label>
        <Button variant="primary" size="lg" disabled={!saved} onClick={onDone}>Verstanden, weitermachen</Button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
// 6. Setup wizard
// ----------------------------------------------------------------
function SetupWizard({ onBack, onComplete }: { onBack: () => void; onComplete: () => void }) {
  const [step, setStep] = useState(0);
  const [token, setToken] = useState('');
  const [tokenOk, setTokenOk] = useState<null | boolean>(null);
  const [form, setForm] = useState({ vorname: '', nachname: '', email: '', telefon: '', passwort: '' });
  const [codes, setCodes] = useState<string[]>([]);
  const [savedCodes, setSavedCodes] = useState(false);
  const [newMasterToken, setNewMasterToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 4: zweiter Admin (optional)
  const [secondAdmin, setSecondAdmin] = useState({ vorname: '', nachname: '', email: '', telefon: '' });
  const [secondAdminTempPw, setSecondAdminTempPw] = useState<string | null>(null);
  const [secondAdminError, setSecondAdminError] = useState<string | null>(null);

  const steps = ['Willkommen', 'Notfall-Schlüssel', 'Admin-Daten', 'Recovery-Codes', 'Zweiter Admin', 'Fertig'];

  const next = () => setStep((s) => Math.min(5, s + 1));
  const prev = () => (step === 0 ? onBack() : setStep((s) => s - 1));

  const verifyToken = async () => {
    setLoading(true);
    try {
      const r = await api.post<{ ok: boolean; noAdmins: boolean }>('/setup/check', { token });
      setTokenOk(r.ok);
      if (r.ok) next();
    } catch {
      setTokenOk(false);
    } finally {
      setLoading(false);
    }
  };

  const createAdmin = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.post<{ codes: string[]; newMasterToken: string; token?: string }>('/setup/first-admin', { token, ...form });
      if (r.token) setToken(r.token);
      setCodes(r.codes);
      setNewMasterToken(r.newMasterToken);
      next();
    } catch (err) {
      if (err instanceof ApiError) setError(err.payload?.error ?? 'Fehler');
    } finally {
      setLoading(false);
    }
  };

  const createSecondAdmin = async () => {
    setSecondAdminError(null);
    if (!secondAdmin.vorname || !secondAdmin.nachname || !secondAdmin.email) {
      setSecondAdminError('Bitte Vorname, Nachname und E-Mail ausfüllen.');
      return;
    }
    setLoading(true);
    try {
      const r = await api.post<{ id: string; tempPasswort: string }>('/users/', {
        ...secondAdmin,
        rolle: 'admin',
        funktion: 'Kassier',
      });
      setSecondAdminTempPw(r.tempPasswort);
    } catch (err) {
      if (err instanceof ApiError && err.payload?.error === 'email_exists') {
        setSecondAdminError('Diese E-Mail ist bereits vergeben.');
      } else {
        setSecondAdminError('Konnte zweiten Admin nicht anlegen.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-shell">
      <div className="auth-card wide">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="auth-logo" style={{ width: 36, height: 36, margin: 0 }}>
            <img src="/logo.png" alt="" style={{ width: 26, height: 26 }} />
          </div>
          <div>
            <div className="t-headline">Ersteinrichtung</div>
            <div className="text-tertiary" style={{ fontSize: 12 }}>Schritt {step + 1} von {steps.length} · {steps[step]}</div>
          </div>
        </div>
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
        </div>

        <div style={{ minHeight: 220 }}>
          {step === 0 && (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <div style={{ width: 56, height: 56, borderRadius: 15, background: 'var(--accent-soft)', color: 'var(--accent)', display: 'grid', placeItems: 'center', margin: '0 auto 14px' }}>
                <PartyPopper size={26} />
              </div>
              <h1 className="t-title-2" style={{ margin: 0 }}>Willkommen bei der Mitgliederverwaltung!</h1>
              <p className="text-secondary" style={{ marginTop: 10, fontSize: 14 }}>
                Lass uns dein Admin-Konto für die Union DSG Gaspoltshofen einrichten.
              </p>
            </div>
          )}
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Field label="Notfall-Schlüssel (Master-Token)" hint="Du findest ihn in der Setup-Dokumentation deines Entwicklers."
                error={tokenOk === false ? 'Token ungültig.' : null}>
                <Input className="t-mono" placeholder="XXXX-XXXX-XXXX-XXXX" value={token} onChange={(e) => { setToken(e.target.value); setTokenOk(null); }} />
              </Field>
              <div className="muted-card" style={{ display: 'flex', gap: 10 }}>
                <ShieldCheck size={16} className="text-accent" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 13 }}>
                  Der Notfall-Schlüssel stellt sicher, dass nur autorisierte Personen das erste Admin-Konto anlegen.
                </span>
              </div>
            </div>
          )}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="grid-2">
                <Field label="Vorname"><Input value={form.vorname} onChange={(e) => setForm({ ...form, vorname: e.target.value })} /></Field>
                <Field label="Nachname"><Input value={form.nachname} onChange={(e) => setForm({ ...form, nachname: e.target.value })} /></Field>
              </div>
              <div className="grid-2">
                <Field label="E-Mail"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                <Field label="Telefon"><Input className="t-mono" value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} /></Field>
              </div>
              <Field label="Passwort">
                <PasswordInput value={form.passwort} onChange={(v) => setForm({ ...form, passwort: v })} placeholder="Mindestens 8 Zeichen" />
              </Field>
              <PasswordStrength pw={form.passwort} />
              {error && <div className="banner danger" style={{ margin: 0 }}><AlertCircle size={18} /><div className="banner-text">{error}</div></div>}
            </div>
          )}
          {step === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="banner warn" style={{ margin: 0 }}>
                <AlertTriangle size={18} />
                <div className="banner-text"><strong>Speichere diese Codes!</strong> Sie werden nur einmal angezeigt.</div>
              </div>
              <div className="codes-grid">
                {codes.map((c, i) => <div className="code-cell" key={i}><span className="idx">{i + 1}</span>{c}</div>)}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="secondary" icon={<Copy size={16} />} onClick={() => navigator.clipboard?.writeText(codes.join('\n'))}>Kopieren</Button>
                <Button variant="secondary" icon={<Printer size={16} />} onClick={() => window.print()}>Drucken</Button>
              </div>
              <label className="row" style={{ gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={savedCodes} onChange={(e) => setSavedCodes(e.target.checked)} style={{ width: 18, height: 18, accentColor: 'var(--accent)' }} />
                <span style={{ fontSize: 14, fontWeight: 500 }}>Ich habe die Codes sicher gespeichert</span>
              </label>
            </div>
          )}
          {step === 4 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {!secondAdminTempPw ? (
                <>
                  <div className="banner warn" style={{ margin: 0 }}>
                    <AlertTriangle size={18} />
                    <div className="banner-text">
                      Wir empfehlen dringend, einen zweiten Admin anzulegen (z. B. den Kassier).
                      So vermeidest du, dass du dich selbst aussperrst.
                    </div>
                  </div>
                  <div className="grid-2">
                    <Field label="Vorname"><Input value={secondAdmin.vorname} onChange={(e) => setSecondAdmin({ ...secondAdmin, vorname: e.target.value })} placeholder="Bernhard" /></Field>
                    <Field label="Nachname"><Input value={secondAdmin.nachname} onChange={(e) => setSecondAdmin({ ...secondAdmin, nachname: e.target.value })} placeholder="Lindner" /></Field>
                  </div>
                  <div className="grid-2">
                    <Field label="E-Mail"><Input type="email" value={secondAdmin.email} onChange={(e) => setSecondAdmin({ ...secondAdmin, email: e.target.value })} placeholder="kassier@…" className="t-mono" /></Field>
                    <Field label="Telefon (optional)"><Input className="t-mono" value={secondAdmin.telefon} onChange={(e) => setSecondAdmin({ ...secondAdmin, telefon: e.target.value })} placeholder="+43 …" /></Field>
                  </div>
                  {secondAdminError && (
                    <div className="banner danger" style={{ margin: 0 }}>
                      <AlertCircle size={18} />
                      <div className="banner-text">{secondAdminError}</div>
                    </div>
                  )}
                  <p className="text-tertiary" style={{ fontSize: 12.5, margin: 0 }}>
                    Der zweite Admin erhält ein temporäres Passwort, das er beim ersten Login ändert.
                    Diesen Schritt kannst du auch überspringen.
                  </p>
                </>
              ) : (
                <>
                  <div className="banner" style={{ margin: 0, background: 'var(--success-soft)', color: 'var(--success)' }}>
                    <Check size={18} />
                    <div className="banner-text">
                      <strong>Zweiter Admin angelegt.</strong> Gib {secondAdmin.vorname} folgende Daten weiter — sie werden nur jetzt angezeigt.
                    </div>
                  </div>
                  <div className="muted-card" style={{ display: 'grid', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                      <span className="t-mono">{secondAdmin.email}</span>
                    </div>
                  </div>
                  <div>
                    <div className="field-label" style={{ marginBottom: 6 }}>Temporäres Passwort</div>
                    <div className="temp-pw">
                      <span className="val">{secondAdminTempPw}</span>
                      <button className="copy" onClick={() => navigator.clipboard?.writeText(secondAdminTempPw)} title="Kopieren">
                        <Copy size={17} />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          {step === 5 && (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <div style={{ width: 56, height: 56, borderRadius: 15, background: 'var(--success-soft)', color: 'var(--success)', display: 'grid', placeItems: 'center', margin: '0 auto 14px' }}>
                <Check size={28} />
              </div>
              <h1 className="t-title-2" style={{ margin: 0 }}>Alles eingerichtet!</h1>
              <p className="text-secondary" style={{ marginTop: 10, fontSize: 14 }}>
                Der Notfall-Schlüssel wurde rotiert. Bewahre den neuen sicher im Vereinstresor auf.
              </p>
              {newMasterToken && (
                <div className="temp-pw" style={{ marginTop: 14, justifyContent: 'center' }}>
                  <span className="val" style={{ fontSize: 18, flex: 'none' }}>{newMasterToken}</span>
                  <button className="copy" onClick={() => navigator.clipboard?.writeText(newMasterToken)}>
                    <Copy size={16} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button variant="ghost" onClick={prev}>{step === 0 ? 'Abbrechen' : 'Zurück'}</Button>
          <div style={{ flex: 1 }} />
          {step === 0 && <Button variant="primary" iconRight={<ArrowRight size={16} />} onClick={next}>Loslegen</Button>}
          {step === 1 && (
            <Button variant="primary" iconRight={<ArrowRight size={16} />} onClick={verifyToken} disabled={loading || !token}>
              {loading && <Loader2 size={17} className="spin" />} Token prüfen
            </Button>
          )}
          {step === 2 && (
            <Button variant="primary" iconRight={<ArrowRight size={16} />} onClick={createAdmin}
              disabled={loading || !form.email || !form.vorname || !form.nachname || form.passwort.length < 8}>
              {loading && <Loader2 size={17} className="spin" />} Admin anlegen
            </Button>
          )}
          {step === 3 && (
            <Button variant="primary" iconRight={<ArrowRight size={16} />} onClick={next} disabled={!savedCodes}>Weiter</Button>
          )}
          {step === 4 && !secondAdminTempPw && (
            <>
              <Button variant="ghost" onClick={next}>Überspringen</Button>
              <Button
                variant="primary"
                iconRight={<ArrowRight size={16} />}
                onClick={createSecondAdmin}
                disabled={loading}
              >
                {loading && <Loader2 size={17} className="spin" />} Zweiten Admin anlegen
              </Button>
            </>
          )}
          {step === 4 && secondAdminTempPw && (
            <Button variant="primary" iconRight={<ArrowRight size={16} />} onClick={next}>Weiter</Button>
          )}
          {step === 5 && (
            <Button variant="primary" icon={<LogIn size={16} />} onClick={onComplete}>Zur App</Button>
          )}
        </div>
      </div>
    </div>
  );
}
